<script setup lang='ts'>
import type { NestProvince } from '@qctsw/common'
import { nestProvince } from '@qctsw/common'
import Item from './item.vue'

const props = withDefaults(defineProps<{
  provinceId?: string
  provinceOption?: NestProvince | null
  cityId?: string
  cityOption?: NestProvince | null
  areaId?: string
  areaOption?: NestProvince | null
  isCity?: boolean
  isArea?: boolean
  itemClass?: string
}>(), {
  provinceId: undefined,
  cityId: undefined,
  areaId: undefined,
  provinceOption: null,
  cityOption: null,
  areaOption: null,
  isCity: true,
  isArea: true,
  itemClass: '',
})
const emits = defineEmits({
  'update:provinceId': (_data: string | undefined) => true,
  'update:provinceOption': (_data: NestProvince | null) => true,
  'update:cityId': (_data: string | undefined) => true,
  'update:cityOption': (_data: NestProvince | null) => true,
  'update:areaId': (_data: string | undefined) => true,
  'update:areaOption': (_data: NestProvince | null) => true,
})

const changeStatus = ref(true)

const provinceSelectId = ref<string | undefined>(undefined)
const provinceSelectOption = ref<NestProvince | null>(null)
function provinceSelectIdHandle(v: string | undefined) {
  clearCity()
  provinceSelectId.value = v
  emits('update:provinceId', v)
}
function provinceSelectHandle(v: NestProvince | null) {
  provinceSelectOption.value = v
  emits('update:provinceOption', v)
}

const citySelectId = ref<string | undefined>(undefined)
const citySelectOption = ref<NestProvince | null>(null)
function citySelectIdHandle(v: string | undefined) {
  clearArea()
  citySelectId.value = v
  emits('update:cityId', v)
}
function citySelectHandle(v: NestProvince | null) {
  citySelectOption.value = v
  emits('update:cityOption', v)
}
function clearCity() {
  citySelectIdHandle(undefined)
  citySelectHandle(null)
}

const areaSelectId = ref<string | undefined>(undefined)
const areaSelectOption = ref<NestProvince | null>(null)
function areaSelectIdHandle(v: string | undefined) {
  areaSelectId.value = v
  emits('update:areaId', v)
}
function areaSelectHandle(v: NestProvince | null) {
  areaSelectOption.value = v
  emits('update:areaOption', v)
}
function clearArea() {
  areaSelectIdHandle(undefined)
  areaSelectHandle(null)

  changeStatus.value = false
}
let timeKey: NodeJS.Timeout | null = null
const stops = [
  watch(changeStatus, () => {
    if (!changeStatus.value) {
      timeKey && clearTimeout(timeKey)
      timeKey = setTimeout(() => {
        changeStatus.value = true
      }, 1)
    }
  }),
  watch(() => [props.provinceId, props.cityId, props.areaId], () => {
    if (provinceSelectId.value !== props.provinceId || !props.provinceId)
      provinceSelectIdHandle(props.provinceId)
    if (citySelectId.value !== props.cityId)
      citySelectIdHandle(props.cityId)
    if (areaSelectId.value !== props.areaId)
      areaSelectIdHandle(props.areaId)
  }, { immediate: true }),
]

onBeforeUnmount(() => {
  timeKey && clearTimeout(timeKey)
  stops.forEach(stop => stop())
})
</script>

<template>
  <div class="grid gap-3 w-full" :class="[isArea ? 'grid-cols-3' : isCity ? 'grid-cols-2' : '']">
    <!-- 省 -->
    <Item
      :id="provinceSelectId"
      :class="itemClass"
      :option="provinceSelectOption"
      :options="nestProvince"
      placeholder="选择省"
      @update:id="provinceSelectIdHandle"
      @update:option="provinceSelectHandle"
    />
    <!-- 市 -->
    <Item
      v-if="isCity && changeStatus"
      :id="citySelectId"
      :class="itemClass"
      placeholder="选择市"
      :option="citySelectOption"
      :options="provinceSelectOption?.children || []"
      @update:id="citySelectIdHandle"
      @update:option="citySelectHandle"
    />
    <!-- 区 -->
    <Item
      v-if="isArea && changeStatus"
      :id="areaSelectId"
      :class="itemClass"
      placeholder="选择区"
      :option="areaSelectOption"
      :options="citySelectOption?.children || []"
      @update:id="areaSelectIdHandle"
      @update:option="areaSelectHandle"
    />
  </div>
</template>
